import {
  useUserMSPCheckHook,
  withErrorBoundary,
  EventEmitterWithIndex,
  EventEmitter
} from '@nile/components';
import { useGlobalChatHook } from '@nile/components/src/Components/GlobalChatHook';
import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { ActionToolbar } from '@nile/mangifera/src/Components/Chat';
import TicketConversationActionHook from '@nile/mangifera/src/Containers/ChatContainers/TicketConversationActionHook';
import { useTicketDetailsConversationStore } from '@nile/mangifera/src/StoreManager/store/ticketDetailsConversationStore';

interface RequesterData {
  userId: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  forgotten: boolean;
}

interface ClientData {
  accountId: string;
  logoFileName?: string;
}

interface TicketConversationData {
  subject: string;
  ticketId: string;
  id: string;
  requester: RequesterData | null;
  client: ClientData | null;
  displayId: string;
  source: string | null;
}

interface TicketConversationActionsProps {
  ticket: TicketConversationData;
}

const TicketConversationActions = (props: TicketConversationActionsProps) => {
  const { ticket } = props;
  const isMSP = useUserMSPCheckHook();
  const intl = useIntl();

  const ticketId = ticket?.ticketId;

  const [isNotesDisabled, setIsNotesDisabled] = useState(false);
  const [isConversationDisabled, setIsConversationDisabled] = useState(false);
  const [sideConversationDisabled, setSideConversationDisabled] = useState(
    false
  );

  const newSideConversationLoading = useTicketDetailsConversationStore(
    (s) => s.newSideConversationLoading
  );

  const { getGlobalConversations } = useGlobalChatHook();

  const globalConversations = getGlobalConversations();
  const globalConversationsRef = React.useRef(null);
  globalConversationsRef.current = globalConversations;

  const checkNotesOrConversationDisabled = () => {
    const currentTicketConversationData = globalConversations?.find(
      (conversation) => conversation?.data?.workItem?.workId === ticketId
    );
    if (currentTicketConversationData) {
      const mode = currentTicketConversationData?.data?.mode;
      if (mode === 'note') {
        setIsNotesDisabled(mode === 'note');
        setIsConversationDisabled(false);
        setSideConversationDisabled(false);
      }
      if (mode === 'conversation') {
        setIsConversationDisabled(mode === 'conversation');
        setIsNotesDisabled(false);
        setSideConversationDisabled(false);
      }
      if (mode === 'NEW_SIDE_CONVERSATION') {
        setSideConversationDisabled(true);
        setIsNotesDisabled(false);
        setIsConversationDisabled(false);
      }
    } else {
      setIsNotesDisabled(false);
      setIsConversationDisabled(false);
      setSideConversationDisabled(false);
    }
  };

  const { openChatPopup, conversationLoading } = TicketConversationActionHook({
    ticket
  });

  const openConversationPopup = (mode) => {
    openChatPopup({ mode });
  };

  const getIsCurrentConversationAvailabe = () => {
    const currentTicketConversationData = globalConversationsRef.current?.find(
      (conversation) => conversation?.id === ticketId
    );
    return currentTicketConversationData;
  };

  const openConversationPopupFromEventEmitter = ({ content, attachments }) => {
    const isCurrentConversationAvailable = getIsCurrentConversationAvailabe();
    if (isCurrentConversationAvailable) {
      const template = {
        content,
        attachments
      };
      EventEmitterWithIndex.dispatch('insertGlobalPopupTemplate', {
        id: ticketId,
        template
      });
    } else {
      openChatPopup({
        mode: 'conversation',
        prefillTemplate: { content, attachments }
      });
    }
  };

  const openTicketNotePopupFromEventEmitter = ({ content }) => {
    openChatPopup({
      mode: 'note',
      prefillTemplate: { content }
    });
  };

  const insertTriageTicketConversationToPopup = (content) => {
    const isCurrentConversationAvailable = getIsCurrentConversationAvailabe();
    if (isCurrentConversationAvailable) {
      const template = {
        content
      };
      EventEmitter.dispatch('insertTriageTicketConversation', {
        id: ticketId,
        template
      });
    } else {
      openChatPopup({
        mode: 'conversation',
        prefillTemplate: { content }
      });
    }
  };

  useEffect(() => {
    checkNotesOrConversationDisabled();
  }, [globalConversations]);

  useEffect(() => {
    const eventIndex = EventEmitterWithIndex.subscribe(
      'insertConversationWithAttachments',
      openConversationPopupFromEventEmitter
    );
    const noteEventIndex = EventEmitterWithIndex.subscribe(
      'insertTicketNote',
      openTicketNotePopupFromEventEmitter
    );

    const triageEventIndex = EventEmitterWithIndex.subscribe(
      'insertTriageTicketConversationToPopup',
      insertTriageTicketConversationToPopup
    );

    return function cleanup() {
      EventEmitterWithIndex.unsubscribe(
        'insertConversationWithAttachments',
        eventIndex
      );
      EventEmitterWithIndex.unsubscribe('insertTicketNote', noteEventIndex);
      EventEmitterWithIndex.unsubscribe(
        'insertTriageTicketConversationToPopup',
        triageEventIndex
      );
    };
  }, []);

  return (
    <ActionToolbar
      onClick={openConversationPopup}
      ticket={{
        requester: ticket.requester
      }}
      createNewSideConversation
      isMSP={isMSP}
      intl={intl}
      conversationLoading={conversationLoading}
      isNotesDisabled={isNotesDisabled}
      isConversationDisabled={isConversationDisabled}
      isSideConversationDisabled={sideConversationDisabled}
      isSideConversationLoading={newSideConversationLoading}
    />
  );
};

export default withErrorBoundary(TicketConversationActions);
